import React, { Suspense, useMemo } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';
import { graphql, useLazyLoadQuery, usePaginationFragment } from 'react-relay';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Grid, Inline, Stack } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import Spinner from '@atlaskit/spinner';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	ExperienceFailure,
	ExperienceStart,
	ExperienceSuccess,
} from '@atlassian/conversation-assistant-instrumentation/ui/ExperienceTracker';
import type { Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { ChatGenericError } from '@atlassian/conversation-assistant-ui-components/ui/ChatGenericError';

import { ROVO_AGENTS_FE_TEAM_NAME } from '../../../../common/constants/private.ts';
import { type AgentCardProps } from '../../../../common/ui/agent-card/index.tsx';
import { mapAgentGqlToAgent } from '../../../../common/utils/agent-query-helper/index.ts';

import type { agentCategorySection_AtlassianConversationAssistantAgent_AgentCategorySectionContent$key } from './__generated__/agentCategorySection_AtlassianConversationAssistantAgent_AgentCategorySectionContent.graphql.ts';
import type { agentCategorySectionPaginationQuery } from './__generated__/agentCategorySectionPaginationQuery.graphql.ts';
import type {
	agentCategorySectionQuery,
	AgentStudioAgentQueryInput,
} from './__generated__/agentCategorySectionQuery.graphql.ts';
import messages from './messages.ts';

const AGENTS_PER_PAGE = 6;

const styles = cssMap({
	grid: {
		gridTemplateColumns: '1fr',
		'@media (min-width: 30rem)': {
			gridTemplateColumns: '1fr 1fr',
		},
	},
	spinner: {
		marginInline: 'auto',
	},
});

export type RenderAgentCard = (args: {
	agent: Agent;
	agentRef: AgentCardProps['agentRef'];
	userPermissionsRef?: AgentCardProps['userPermissionsRef'];
	hideDescription: boolean;
}) => React.ReactNode;

type AgentCategorySectionContentProps = {
	input?: AgentStudioAgentQueryInput;
	sectionTitle: string;
	renderAgentCard: RenderAgentCard;
	/**
	 * Rendered in place of the agents grid when the section has no agents (and
	 * is not currently loading). Use this to surface a section-specific empty
	 * state, e.g. "No agents created yet".
	 */
	emptyState?: React.ReactNode;
} & AssistanceServiceInputParam;

const AgentCategorySectionContent = ({
	input,
	assistanceServiceParams: { siteId, product, headers: assistanceServiceHeaders },
	sectionTitle,
	renderAgentCard,
	emptyState,
}: AgentCategorySectionContentProps) => {
	const { formatMessage } = useIntl();
	const headers = new Headers(assistanceServiceHeaders);
	const maybeWorkspaceId = headers.get('x-workspace-id');

	const preloadedRef = useLazyLoadQuery<agentCategorySectionQuery>(
		graphql`
			query agentCategorySectionQuery(
				$cloudId: ID!
				$cloudIdString: String!
				$input: AgentStudioAgentQueryInput
				$product: String
				$first: Int
				$workspaceId: String
				$skipUserPermissions: Boolean = true
				$enableJiraAgentFields: Boolean = false
			) {
				...agentCategorySection_AtlassianConversationAssistantAgent_AgentCategorySectionContent
					@arguments(
						cloudId: $cloudIdString
						input: $input
						product: $product
						first: $first
						workspaceId: $workspaceId
						enableJiraAgentFields: $enableJiraAgentFields
						cloudIdAsId: $cloudId
					)
				atlassianStudio_userSiteContext(cloudId: $cloudId) @skip(if: $skipUserPermissions) {
					... on AtlassianStudioUserSiteContextOutput {
						userPermissions {
							# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
							...agentVerificationDropdownItem_AtlaskitRovoAgentComponents_userPermissionsRef
						}
					}
				}
			}
		`,
		{
			cloudId: siteId,
			cloudIdString: siteId,
			input,
			product,
			first: AGENTS_PER_PAGE,
			workspaceId: maybeWorkspaceId,
			skipUserPermissions: !fg('rovo_agents_agent_verification'),
		},
	);

	const { data, hasNext, isLoadingNext, loadNext } = usePaginationFragment<
		agentCategorySectionPaginationQuery,
		agentCategorySection_AtlassianConversationAssistantAgent_AgentCategorySectionContent$key
	>(
		/* eslint-disable @atlassian/relay/unused-fields */
		/* These fields are used in the mapAgentGqlToAgent function */
		graphql`
			fragment agentCategorySection_AtlassianConversationAssistantAgent_AgentCategorySectionContent on Query
			@argumentDefinitions(
				first: { type: "Int" }
				after: { type: "String" }
				cloudId: { type: "String!" }
				input: { type: "AgentStudioAgentQueryInput" }
				product: { type: "String" }
				workspaceId: { type: "String" }
				enableJiraAgentFields: { type: "Boolean!" }
				cloudIdAsId: { type: "ID!" }
			)
			@refetchable(queryName: "agentCategorySectionPaginationQuery") {
				agentStudio_getAgents(
					cloudId: $cloudId
					input: $input
					first: $first
					after: $after
					product: $product
					workspaceId: $workspaceId
				)
					@optIn(to: "AgentStudio")
					# This connection key is shared between the panel, fullscreen, modal and favourites in the modal - be careful when changing it
					@connection(key: "paginatedAgentsList_agentStudio_getAgents") {
					edges {
						node {
							id
							name
							deactivated
							description
							isFavourite
							creatorType
							conversationStarters
							identityAccountId
							externalConfigReference
							creator {
								id
							}
							icon
							authoringTeam {
								id
								profileUrl
								displayName
								smallAvatarImageUrl
							}
							forgeCreator
							# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
							...agentVerified_AtlaskitRovoAgentComponents
							# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
							...agentVerificationDropdownItem_AtlaskitRovoAgentComponents_agentRef
							# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
							...agentCard_AtlassianConversationAssistantAgent_agentRef
								@arguments(enableJiraAgentFields: $enableJiraAgentFields, cloudId: $cloudIdAsId)
						}
					}
				}
			}
		`,
		preloadedRef,
	);

	const agents = useMemo(
		() =>
			data?.agentStudio_getAgents?.edges
				.map((edge) =>
					edge.node
						? {
								agent: mapAgentGqlToAgent(edge.node),
								agentRef: edge.node,
							}
						: null,
				)
				.filter((i): i is NonNullable<typeof i> => Boolean(i)),
		[data?.agentStudio_getAgents?.edges],
	);

	/**
	 * Only show the top right chevron for the first page. If there are more agents, show the load more button at the bottom.
	 */
	const shouldShowMoreChevron =
		!isLoadingNext && hasNext && agents && agents.length <= AGENTS_PER_PAGE;
	const shouldShowLoadMoreButton =
		!isLoadingNext && hasNext && agents && agents.length > AGENTS_PER_PAGE;

	const isEmpty = !isLoadingNext && !hasNext && (!agents || agents.length === 0);

	return (
		<Stack space="space.100">
			<Inline spread="space-between" alignBlock="center">
				<Heading size="small" as="h2">
					{sectionTitle}
				</Heading>

				{shouldShowMoreChevron && (
					<Button
						appearance="subtle"
						iconBefore={ChevronDownIcon}
						onClick={() => loadNext(AGENTS_PER_PAGE)}
					>
						{formatMessage(messages.moreButton)}
					</Button>
				)}
			</Inline>

			{isEmpty && emptyState ? (
				emptyState
			) : (
				<Grid gap="space.100" xcss={styles.grid}>
					{agents?.map((agent) =>
						renderAgentCard({
							agent: agent.agent,
							agentRef: agent.agentRef,
							userPermissionsRef: preloadedRef.atlassianStudio_userSiteContext?.userPermissions,
							hideDescription: true,
						}),
					)}
				</Grid>
			)}

			{isLoadingNext && (
				<Box xcss={styles.spinner}>
					<Spinner />
				</Box>
			)}

			{shouldShowLoadMoreButton && (
				<Inline alignInline="center" alignBlock="center">
					<Button
						appearance="subtle"
						isLoading={isLoadingNext}
						onClick={() => loadNext(AGENTS_PER_PAGE)}
					>
						{formatMessage(messages.loadMoreButton)}
					</Button>
				</Inline>
			)}
		</Stack>
	);
};

export const AgentCategorySection = (
	props: AgentCategorySectionContentProps,
): React.JSX.Element => {
	return (
		<ErrorBoundary
			fallbackRender={({ error }) => (
				<>
					<ExperienceFailure error={error} name={ExperienceName.AGENTS_FULLSCREEN_AGENT_LIST} />
					<ChatGenericError errorType="generic" />
				</>
			)}
			onError={(error) => {
				logException({
					exception: error,
					name: ExperienceName.AGENTS_FULLSCREEN_AGENT_LIST,
					tags: {
						siteId: props.assistanceServiceParams.siteId,
						teamName: ROVO_AGENTS_FE_TEAM_NAME,
						category: props.sectionTitle,
					},
				});
			}}
		>
			<Suspense
				fallback={
					<Stack space="space.100">
						<Inline spread="space-between" alignBlock="center">
							<Skeleton height={20} width={100} />
							<Skeleton height={20} width={40} />
						</Inline>

						<Grid gap="space.100" xcss={styles.grid}>
							{Array.from({ length: AGENTS_PER_PAGE }).map((_, index) => (
								<Skeleton key={index} height={68} width="100%" />
							))}
						</Grid>
					</Stack>
				}
			>
				<ExperienceStart name={ExperienceName.AGENTS_FULLSCREEN_AGENT_LIST} />
				<AgentCategorySectionContent {...props} />
				<ExperienceSuccess name={ExperienceName.AGENTS_FULLSCREEN_AGENT_LIST} />
			</Suspense>
		</ErrorBoundary>
	);
};
